import { Injectable } from "@angular/core";
import { BehaviorSubject, combineLatest, map, Observable, shareReplay, switchMap } from "rxjs";
import { ApolloClientService } from "./apollo-client.service";
import { Restriction } from "../models/restriction";
import { RestrictionsQueries } from "../graphql/queries/restrictions.queries";
import {
  convertGraphqlRestrictionOutputToRestriction,
  convertGraphqlVehicleWithRestrictionsOutputToVehicleWithRestriction,
  convertGraphqlFpcWithRestrictionsOutputToFpcWithRestrictions
} from "../graphql/mappers/restriction.mappers";
import { GraphqlRestrictionDto } from "../graphql/models/output/restriction-output.model";
import { GraphqlVehicleWithRestrictionDto } from "../graphql/models/output/vehicle-with-restriction-output.model";
import { GraphqlFpcWithRestrictionsDto } from "../graphql/models/output/fpc-with-restrictions-output.model";
import { VehicleWithRestrictions } from "../models/vehicle-with-restrictions";
import { FpcWithRestrictions } from "../models/fpc-with-restrictions";
import { DrivingRestrictionMutations } from 'src/app/graphql/mutations/restrictions.mutations';

@Injectable({ providedIn: 'root' })
export class RestrictionsService {

  private refreshTick$ = new BehaviorSubject<number>(0);
  private currentVehicleNameSubject = new BehaviorSubject<string | undefined>(undefined);

  public restrictions$ = combineLatest({
    tick: this.refreshTick$,
    vehicleName: this.currentVehicleNameSubject
  }).pipe(
    switchMap(({ vehicleName }) => this.getAllRestrictions$(vehicleName)),
    map(list =>
      [...list].sort((a, b) => ((a.restrictionId ?? '') > (b.restrictionId ?? '') ? 1 : -1))
    ),
    shareReplay(1)
  );

  constructor(private apolloClientService: ApolloClientService) {}

  public setCurrentVehicleName(vehicleName: string | undefined) {
    this.currentVehicleNameSubject.next(vehicleName);
  }

  public clearCurrentVehicleName() {
    this.currentVehicleNameSubject.next(undefined);
  }

  public refreshRestrictions() {
    this.refreshTick$.next(this.refreshTick$.value + 1);
  }

  public getAllRestrictions$(vehicleName?: string): Observable<Restriction[]> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      switchMap(client =>
        client.query<{ drivingRestrictionGetRestrictions: GraphqlRestrictionDto[] }>({
          query: RestrictionsQueries.getAllRestrictions,
          variables: { vehicleName },
          fetchPolicy: 'network-only'
        })
      ),
      map(result =>
        (result.data?.drivingRestrictionGetRestrictions ?? []).map(r =>
          convertGraphqlRestrictionOutputToRestriction(r)
        )
      )
    );
  }

  private vehiclesWithRestrictions$ = this.refreshTick$.pipe(
    switchMap(() => this.apolloClientService.abstractionLayerClient$),
    switchMap(client =>
      client.query<{ drivingRestrictionGetVehicleRestrictions: GraphqlVehicleWithRestrictionDto[] }>({
        query: RestrictionsQueries.getAllVehiclesWithRestrictions,
        variables: {},
        fetchPolicy: 'network-only'
      })
    ),
    map(result =>
      (result.data?.drivingRestrictionGetVehicleRestrictions ?? []).map(vwr =>
        convertGraphqlVehicleWithRestrictionsOutputToVehicleWithRestriction(vwr)
      )
    ),
    shareReplay(1)
  );

  public getAllVehiclesWithRestrictions$(): Observable<VehicleWithRestrictions[]> {
    return this.vehiclesWithRestrictions$;
  }

  public getFpcWithRestrictions$(fpcId: string): Observable<FpcWithRestrictions | null> {
    return this.getAllFpcsWithRestrictions$().pipe(
      map(fpcs => fpcs.find(f => f.fpcId === fpcId) ?? null)
    );
  }

  private fpcsWithRestrictions$ = this.refreshTick$.pipe(
    switchMap(() => this.apolloClientService.abstractionLayerClient$),
    switchMap(client =>
      client.query<{ drivingRestrictionGetFpcRestrictions: GraphqlFpcWithRestrictionsDto[] }>({
        query: RestrictionsQueries.getAllFpcsWithRestrictions,
        variables: {},
        fetchPolicy: 'network-only'
      })
    ),
    map(result =>
      (result.data?.drivingRestrictionGetFpcRestrictions ?? []).map(f =>
        convertGraphqlFpcWithRestrictionsOutputToFpcWithRestrictions(f)
      )
    ),
    shareReplay(1)
  );

  public getAllFpcsWithRestrictions$(): Observable<FpcWithRestrictions[]> {
    return this.fpcsWithRestrictions$;
  }

  public deleteRestriction$(restrictionId: string): Observable<any> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DrivingRestrictionMutations.deleteRestriction,
          variables: { restrictionId },
          context: {
            headers: { 'ShowLoading': 'true' }
          }
        })
      )
    );
  }

  public getArchivedRestrictions$(): Observable<Restriction[]> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      switchMap(client =>
        client.query<{ drivingRestrictionGetArchived: GraphqlRestrictionDto[] }>({
          query: RestrictionsQueries.getArchivedRestrictions,
          fetchPolicy: 'network-only'
        })
      ),
      map(result =>
        (result.data?.drivingRestrictionGetArchived ?? []).map(r =>
          convertGraphqlRestrictionOutputToRestriction(r)
        )
      )
    );
  }

  public endRestriction$(restrictionId: string): Observable<any> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      switchMap(client =>
        client.mutate({
          mutation: DrivingRestrictionMutations.endRestriction,
          variables: { restrictionId },
          context: {
            headers: { 'ShowLoading': 'true' }
          }
        })
      )
    );
  }
}
